import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
// Tab Imports
import { BasicTab } from '../components/ProductFormTabs/BasicTab';
import { ClassificationTab } from '../components/ProductFormTabs/ClassificationTab';
import { LoanAmountTab } from '../components/ProductFormTabs/LoanAmountTab';
import { TenorTab } from '../components/ProductFormTabs/TenorTab';
import { RepaymentTab } from '../components/ProductFormTabs/RepaymentTab';
import { DisbursementTab } from '../components/ProductFormTabs/DisbursementTab';
import { KYCTab } from '../components/ProductFormTabs/KYCTab';
import { InterestTab } from '../components/ProductFormTabs/InterestTab';
import { PenaltiesTab } from '../components/ProductFormTabs/PenaltiesTab';
import { CreditScoreTab } from '../components/ProductFormTabs/CreditScoreTab';
import { TransactionsTab } from '../components/ProductFormTabs/TransactionsTab';
import { RuleBuilderTab } from '../components/ProductFormTabs/RuleBuilderTab';
import { AccountsTab } from '../components/ProductFormTabs/AccountsTab';
import { EventFeesTab } from '../components/ProductFormTabs/EventFeesTab';
import { CoolingOffTab } from '../components/ProductFormTabs/CoolingOffTab';
interface ProductFormProps {
  onBack: () => void;
  templateId: string | null;
}
export function ProductForm({
  onBack,
  templateId
}: ProductFormProps) {
  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const tabs = [{
    id: 'basic',
    label: 'Basic'
  }, {
    id: 'classification',
    label: 'Classification'
  }, {
    id: 'amount',
    label: 'Loan Amount'
  }, {
    id: 'tenor',
    label: 'Tenor'
  }, {
    id: 'repayment',
    label: 'Repayment'
  }, {
    id: 'disbursement',
    label: 'Disbursement'
  }, {
    id: 'kyc',
    label: 'KYC Group'
  }, {
    id: 'interest',
    label: 'Interest/Markup'
  }, {
    id: 'penalties',
    label: 'Penalties'
  }, {
    id: 'cooling-off',
    label: 'Cooling Off Period'
  }, {
    id: 'event-fees',
    label: 'Event Fees'
  }, {
    id: 'credit',
    label: 'Credit Score'
  }, {
    id: 'transactions',
    label: 'Transactions'
  }, {
    id: 'rules',
    label: 'Rule Builder'
  }, {
    id: 'accounts',
    label: 'GL Account'
  }];
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      onBack();
    }, 1500);
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return <BasicTab data-id="element-2169" />;
      case 'classification':
        return <ClassificationTab data-id="element-2170" />;
      case 'amount':
        return <LoanAmountTab data-id="element-2171" />;
      case 'tenor':
        return <TenorTab data-id="element-2172" />;
      case 'repayment':
        return <RepaymentTab data-id="element-2173" />;
      case 'disbursement':
        return <DisbursementTab data-id="element-2174" />;
      case 'kyc':
        return <KYCTab data-id="element-2175" />;
      case 'interest':
        return <InterestTab data-id="element-2176" />;
      case 'penalties':
        return <PenaltiesTab data-id="element-2177" />;
      case 'cooling-off':
        return <CoolingOffTab data-id="element-2178" />;
      case 'event-fees':
        return <EventFeesTab data-id="element-2179" />;
      case 'credit':
        return <CreditScoreTab data-id="element-2180" />;
      case 'transactions':
        return <TransactionsTab data-id="element-2181" />;
      case 'rules':
        return <RuleBuilderTab data-id="element-2182" />;
      case 'accounts':
        return <AccountsTab data-id="element-2183" />;
      default:
        return <BasicTab data-id="element-2184" />;
    }
  };
  return <div className="flex flex-col h-full" data-id="element-2185">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-2186">
        <div className="px-6 py-4" data-id="element-2187">
          <div className="flex items-center justify-between" data-id="element-2188">
            <div className="flex items-center" data-id="element-2189">
              <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-2190">
                <ArrowLeft className="h-5 w-5" data-id="element-2191" />
              </button>
              <div data-id="element-2192">
                <h1 className="text-lg font-bold text-gray-900" data-id="element-2193">
                  {templateId ? 'New Product from Template' : 'New Loan Product'}
                </h1>
                <p className="text-xs text-gray-500" data-id="element-2194">Configuration Draft</p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-2195">
              <Button variant="secondary" onClick={onBack} data-id="element-2196">
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isSaving} data-id="element-2197">
                <Save className="mr-2 h-4 w-4" data-id="element-2198" />
                Save Product
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="px-6" data-id="element-2199">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-2200" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-y-auto" data-id="element-2201">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[500px]" data-id="element-2202">
          <div className="mb-6 pb-4 border-b border-gray-100 flex items-center justify-between" data-id="element-2203">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-2204">
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            <div className="flex items-center text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-2205">
              <AlertCircle className="w-4 h-4 mr-2" data-id="element-2206" />
              <span data-id="element-2207">All changes are auto-saved to draft</span>
            </div>
          </div>

          {renderTabContent()}

          {/* Navigation Footer within Content */}
          <div className="mt-12 pt-6 border-t border-gray-100 flex justify-between" data-id="element-2208">
            <Button variant="secondary" disabled={activeTab === tabs[0].id} onClick={() => {
            const idx = tabs.findIndex(t => t.id === activeTab);
            if (idx > 0) setActiveTab(tabs[idx - 1].id);
          }} data-id="element-2209">
              Previous Tab
            </Button>
            <Button variant="primary" disabled={activeTab === tabs[tabs.length - 1].id} onClick={() => {
            const idx = tabs.findIndex(t => t.id === activeTab);
            if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1].id);
          }} data-id="element-2210">
              Next Tab
            </Button>
          </div>
        </div>
      </main>
    </div>;
}